"use client";

import { useEffect, useState } from "react";
import mainAPI from "../../services/main/mainAPI";
import recommendGameAPI from "../../services/recommendGame/recommendGameAPI";
import RecommendGamePopup from "../popup/recommendGame";
import CountAllGame from "../countAllGame";

type RecommendGame = {
    id: number;
    uuid?: string;
    exercise_name: string;
    game_type: string;
    thumbnail: string | null;
    subject?: string;
    create_by?: string;
};

function getThumbnailUrl(path: string | null | undefined): string | null {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `https://thaipilacreate.eef.or.th/api${path}`;
}

function gameTypeLabel(gameType: string): string {
    const map: Record<string, string> = {
        situation: "Situation",
        find_the_match: "Find the Match",
        game_show_quiz: "Game Show Quiz",
        whack_a_mole: "Whack a Mole",
        "air_plan/flying_fruits": "Flying Fruits",
        flip_cards: "Flip Cards",
        flappy_bird: "Flappy Chicken",
        complete_the_sentence: "Complete the Sentence",
        anagram: "Anagram",
    };
    return map[gameType] ?? gameType;
}

export default function ManageHome() {
    const [gameCount, setGameCount] = useState<number | null>(null);
    const [recommendGames, setRecommendGames] = useState<RecommendGame[]>([]);
    const [isLoadingCount, setIsLoadingCount] = useState(false);
    const [isLoadingRecommend, setIsLoadingRecommend] = useState(false);
    const [showPopup, setShowPopup] = useState(false);

    const fetchCount = async () => {
        setIsLoadingCount(true);
        try {
            const res = await mainAPI.countGameExceptGameDefault();
            if (res && typeof (res as { count?: unknown }).count !== "undefined") {
                setGameCount(Number((res as { count: string }).count));
            }
        } catch {
            console.log("Failed to fetch game count");
        } finally {
            setIsLoadingCount(false);
        }
    };

    const fetchRecommendGames = async () => {
        setIsLoadingRecommend(true);
        try {
            const res = await recommendGameAPI.getAllRecommendGames();
            if (Array.isArray(res)) {
                setRecommendGames(res as RecommendGame[]);
            } else if (res && Array.isArray((res as { data?: unknown }).data)) {
                setRecommendGames((res as { data: RecommendGame[] }).data);
            }
        } catch {
            console.log("Failed to fetch recommend games");
        } finally {
            setIsLoadingRecommend(false);
        }
    };

    useEffect(() => {
        fetchCount();
        fetchRecommendGames();
    }, []);

    const handlePopupClose = () => {
        setShowPopup(false);
        fetchRecommendGames();
    };

    return (
        <div className="space-y-5">
            <div>
                <CountAllGame />
            </div>
            <div className="rounded-xl border border-[#dcdcdc] bg-white shadow-sm">
                <div className="flex items-center justify-between px-5 py-4">
                    <h2 className="text-xl font-bold text-[#2b2b2b]">จัดการหน้า Home - เกมแนะนำ</h2>
                    <button
                        type="button"
                        onClick={() => setShowPopup(true)}
                        className="flex items-center gap-1.5 rounded-lg bg-[#FDEF5C] hover:bg-[#f0dc30] px-4 py-2 text-lg font-normal text-[#2b2b2b] transition cursor-pointer"
                    >
                        <span className="text-lg font-bold leading-none">+</span>
                        เพิ่มเกมแนะนำ
                    </button>
                </div>

                <div className="p-5">
                    {isLoadingRecommend ? (
                        <div className="flex justify-center py-8">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
                        </div>
                    ) : recommendGames.length === 0 ? (
                        <p className="py-8 text-center text-sm text-[#aaa]">ยังไม่มีเกมแนะนำ</p>
                    ) : (
                        <div className="flex flex-wrap gap-4">
                            {recommendGames.map((game) => {
                                const thumbUrl = getThumbnailUrl(game.thumbnail);
                                return (
                                    <div
                                        key={game.id}
                                        className="w-70.5 overflow-hidden rounded-xl border border-[#e8e8e8] bg-[#fafafa] shadow-sm transition hover:shadow-md"
                                    >
                                        <div className="relative h-39.75 w-70.5 overflow-hidden bg-[#e0e0e0]">
                                            {thumbUrl ? (
                                                <img
                                                    src={thumbUrl}
                                                    alt={game.exercise_name}
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : (
                                                <div className="flex h-full w-full items-center justify-center text-xs text-[#bbb]">
                                                    ไม่มีรูป
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-3">
                                            <p className="truncate text-xl font-bold text-[#2b2b2b]">
                                                {game.exercise_name}
                                            </p>
                                            {game.create_by && (
                                                <p className="mt-0.5 truncate text-lg text-[#2B2B2B]">
                                                    โดย {game.create_by}
                                                </p>
                                            )}
                                            <div className="mt-2">
                                                <span className="inline-block rounded-full bg-[#FDEF5C] px-2.5 py-0.5 text-xs font-semibold text-[#2B2B2B]">
                                                    {gameTypeLabel(game.game_type)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {showPopup && <RecommendGamePopup onClose={handlePopupClose} />}
        </div>
    );
}
